import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppBackButton from '../common/AppBackButton';
import ReferencePopup from '../Popups/ReferencePopup';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { useSessionStore } from '../../store/sessionStore';
import { ReturnedReference } from '../Create-Portfolio/types';
import { getPortfolioReferences } from '../../utils/portfolioReferences';
import {
  getReferenceFeedbackSummary,
  parseReferenceVerificationAnswers,
} from '../../utils/referenceFeedback';
import {
  referenceResendError,
  resendReferenceVerificationEmail,
} from '../../utils/referenceResend';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import {
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Filter,
  HelpCircle,
  Mail,
  MoreVertical,
  Search,
  Send,
  UserPlus,
  Users,
} from 'lucide-react';
import './ReferenceFeedbackListPage.css';

type RefRow = ReturnedReference & {
  verification_sent_at?: string | null;
  verified_at?: string | null;
};

type StatusFilter = 'all' | 'pending' | 'completed';

function initialsFrom(name: string): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function parseIso(iso?: string | Date | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatRefDate(iso?: string | Date | null): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatRefDateTime(iso?: string | Date | null): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return `${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} at ${d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function daysAgoLabel(iso?: string | Date | null): string {
  const d = parseIso(iso);
  if (!d) return '—';
  const n = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  if (n === 0) return 'today';
  if (n === 1) return '1 day ago';
  return `${n} days ago`;
}

function invitationLabel(sentAt?: string | Date | null): string {
  const d = parseIso(sentAt);
  if (!d) return 'Invitation not sent yet';
  const n = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (n === 0) return `Invitation sent today at ${time}`;
  if (n === 1) return `Invitation sent yesterday at ${time}`;
  return `Invitation sent ${formatRefDate(sentAt)} at ${time}`;
}

function openGlobalHelp() {
  document.querySelector<HTMLButtonElement>('.help-fab')?.click();
}

const ReferenceFeedbackListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [references, setReferences] = useState<RefRow[]>([]);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const portfolio = useSessionStore((s) => s.portfolio);

  const loadReferences = useCallback(async () => {
    setLoading(true);
    try {
      const p =
        useSessionStore.getState().portfolio ??
        (await useSessionStore.getState().refreshPortfolio());
      setReferences(getPortfolioReferences(p) as RefRow[]);
    } catch {
      setReferences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReferences();
  }, [loadReferences]);

  useEffect(() => {
    if (!portfolio) return;
    setReferences(getPortfolioReferences(portfolio) as RefRow[]);
  }, [portfolio]);

  useEffect(() => {
    if (openMenuId == null) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const stats = useMemo(() => {
    const total = references.length;
    const pending = references.filter((r) => !r.isReferenceConfirmed).length;
    const completed = references.filter((r) => r.isReferenceConfirmed).length;
    const emailsSent = references.filter(
      (r) => r.verification_sent_at || (r.email && r.created_at)
    ).length;
    return { total, pending, completed, emailsSent };
  }, [references]);

  const filteredReferences = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return references.filter((ref) => {
      if (statusFilter === 'pending' && ref.isReferenceConfirmed) {
        return false;
      }
      if (statusFilter === 'completed' && !ref.isReferenceConfirmed) {
        return false;
      }
      if (!q) return true;
      const hay = `${ref.name} ${ref.email} ${ref.role} ${ref.organization}`.toLowerCase().trim();
      return hay.includes(q);
    });
  }, [references, searchQuery, statusFilter]);

  const handleResend = async (ref: RefRow) => {
    if (!ref.email?.trim()) {
      toast.error('This reference does not have an email address. Add an email before resending.');
      return;
    }
    setResendingId(ref.id);
    try {
      const detail = await resendReferenceVerificationEmail(ref.id);
      toast.success(detail);
      await loadReferences();
    } catch (err) {
      toast.error(referenceResendError(err));
    } finally {
      setResendingId(null);
    }
  };

  const handleRemove = async (ref: RefRow) => {
    setOpenMenuId(null);
    if (!window.confirm(`Remove ${ref.name} from your references? This cannot be undone.`)) {
      return;
    }
    setRemovingId(ref.id);
    try {
      await axiosInstance.delete(`${API_ROUTES.REFERENCE.UPDATE_REFERENCE}/${ref.id}`);
      toast.success('Reference removed.');
      await loadReferences();
    } catch {
      toast.error('Could not remove this reference. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const goToDetail = (refId: number) => {
    navigate(`/dashboard/references/${refId}`, {
      state: { from: '/dashboard/references' },
    });
  };

  if (loading && references.length === 0) {
    return <PageBusyLoader pageName="Reference Feedback" />;
  }

  return (
    <div className="v1-dashboard-page ref-fb-page">
      <main className="v1-dashboard-main ref-fb-main">
        <header className="ref-fb-top">
          <div className="ref-fb-top-left">
            <div className="ref-fb-nav-row">
              <AppBackButton to="/dashboard" />
            </div>
            <h1 className="ref-fb-title">Reference Feedback</h1>
            <p className="ref-fb-lead">
              Verification status and feedback from your portfolio references.
            </p>
          </div>
          <div className="ref-fb-top-help">
            <button type="button" className="ref-fb-help-link" onClick={openGlobalHelp}>
              <HelpCircle size={16} aria-hidden />
              Need help?
            </button>
            <button type="button" className="ref-fb-help-btn" onClick={openGlobalHelp}>
              Get Help
            </button>
          </div>
        </header>

        <section className="ref-fb-stats" aria-label="Reference summary">
          <div className="ref-fb-stat-card">
            <span className="ref-fb-stat-ico green" aria-hidden>
              <Users size={20} />
            </span>
            <div className="ref-fb-stat-body">
              <span className="ref-fb-stat-label">Total References</span>
              <strong className="ref-fb-stat-value">{stats.total}</strong>
              <span className="ref-fb-stat-sub">Added to your profile</span>
            </div>
          </div>
          <div className="ref-fb-stat-card">
            <span className="ref-fb-stat-ico amber" aria-hidden>
              <Clock size={20} />
            </span>
            <div className="ref-fb-stat-body">
              <span className="ref-fb-stat-label">Pending</span>
              <strong className="ref-fb-stat-value">{stats.pending}</strong>
              <span className="ref-fb-stat-sub">Awaiting verification</span>
            </div>
          </div>
          <div className="ref-fb-stat-card">
            <span className="ref-fb-stat-ico emerald" aria-hidden>
              <CheckCircle2 size={20} />
            </span>
            <div className="ref-fb-stat-body">
              <span className="ref-fb-stat-label">Completed</span>
              <strong className="ref-fb-stat-value">{stats.completed}</strong>
              <span className="ref-fb-stat-sub">References verified</span>
            </div>
          </div>
          <div className="ref-fb-stat-card">
            <span className="ref-fb-stat-ico purple" aria-hidden>
              <Mail size={20} />
            </span>
            <div className="ref-fb-stat-body">
              <span className="ref-fb-stat-label">Emails Sent</span>
              <strong className="ref-fb-stat-value">{stats.emailsSent}</strong>
              <span className="ref-fb-stat-sub">Total invitations sent</span>
            </div>
          </div>
        </section>

        <div className="ref-fb-toolbar">
          <div className="ref-fb-search-wrap">
            <Search size={18} aria-hidden />
            <input
              type="search"
              className="ref-fb-search"
              placeholder="Search references by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search references"
            />
          </div>
          <div className="ref-fb-filter-wrap">
            <Filter size={16} className="filter-ico" aria-hidden />
            <select
              className="ref-fb-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              aria-label="Filter by status"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <section className="ref-fb-add-banner">
          <div className="ref-fb-add-left">
            <span className="ref-fb-add-ico" aria-hidden>
              <UserPlus size={22} />
            </span>
            <div>
              <h3 className="ref-fb-add-title">Add more references</h3>
              <p className="ref-fb-add-text">
                Add more references to strengthen your profile and improve your verification.
              </p>
            </div>
          </div>
          <button type="button" className="ref-fb-add-btn" onClick={() => setShowAddModal(true)}>
            Add Reference
          </button>
        </section>

        {filteredReferences.length === 0 ? (
          <div className="ref-fb-empty">
            <p>
              {references.length === 0
                ? 'No references added yet. Add your first reference to get started.'
                : 'No references match your search or filter.'}
            </p>
            {references.length === 0 ? (
              <button
                type="button"
                className="ref-fb-add-btn"
                onClick={() => setShowAddModal(true)}
              >
                Add Reference
              </button>
            ) : null}
          </div>
        ) : (
          <div className="ref-fb-list">
            {filteredReferences.map((ref) => {
              const isVerified = Boolean(ref.isReferenceConfirmed);
              const answers = parseReferenceVerificationAnswers(ref.verificationAnswers);
              const summary = getReferenceFeedbackSummary(answers);
              const sentAt = ref.verification_sent_at ?? ref.created_at;
              const canResend = !isVerified && !!ref.email;

              return (
                <article key={ref.id} className="ref-fb-card">
                  <div className="ref-fb-avatar" aria-hidden>
                    {initialsFrom(ref.name || '')}
                  </div>

                  <div className="ref-fb-card-main">
                    <h2 className="ref-fb-card-name">{ref.name}</h2>
                    {ref.role ? <p className="ref-fb-card-role">{ref.role}</p> : null}
                    {ref.email ? (
                      <p className="ref-fb-card-email">
                        <Mail size={14} aria-hidden />
                        <a href={`mailto:${ref.email}`}>{ref.email}</a>
                      </p>
                    ) : null}
                    <div className="ref-fb-card-meta">
                      <span>
                        <Calendar size={13} aria-hidden />
                        Added {daysAgoLabel(ref.created_at)}
                      </span>
                      <span>{invitationLabel(sentAt)}</span>
                    </div>

                    <div className="ref-fb-card-status-row">
                      <span className={`ref-fb-badge ${isVerified ? 'verified' : 'pending'}`}>
                        {isVerified ? (
                          <CheckCircle2 size={12} aria-hidden />
                        ) : (
                          <Clock size={12} aria-hidden />
                        )}
                        {isVerified ? 'Verified' : 'Pending'}
                      </span>
                      {!isVerified ? (
                        <p className="ref-fb-waiting-text">
                          Waiting for {ref.name} to complete verification.
                        </p>
                      ) : null}
                    </div>

                    {isVerified && summary ? <p className="ref-fb-summary">{summary}</p> : null}

                    {!isVerified && sentAt ? (
                      <div className="ref-fb-card-status-row">
                        <span className="ref-fb-last-sent">
                          <Mail size={12} aria-hidden />
                          <span>
                            <strong>Last email sent</strong>
                            <br />
                            {formatRefDateTime(sentAt)}
                          </span>
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className="ref-fb-card-actions">
                    <div className="ref-fb-menu-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="ref-fb-menu-btn"
                        aria-label="More options"
                        aria-expanded={openMenuId === ref.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId((id) => (id === ref.id ? null : ref.id));
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuId === ref.id ? (
                        <div className="ref-fb-menu" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpenMenuId(null);
                              goToDetail(ref.id);
                            }}
                          >
                            View details
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="danger"
                            disabled={removingId === ref.id}
                            onClick={() => void handleRemove(ref)}
                          >
                            {removingId === ref.id ? 'Removing…' : 'Remove reference'}
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="ref-fb-action-btns">
                      {canResend ? (
                        <button
                          type="button"
                          className="ref-fb-btn-outline"
                          disabled={resendingId === ref.id}
                          onClick={() => void handleResend(ref)}
                        >
                          <Send size={14} aria-hidden />
                          {resendingId === ref.id ? 'Sending…' : 'Resend Email'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="ref-fb-view-link"
                        onClick={() => goToDetail(ref.id)}
                      >
                        View Details
                        <ChevronRight size={14} aria-hidden />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          centered
          className="ss-app-modal ref-check-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Add reference</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ReferencePopup
              embedded
              hideEmbeddedMeta
              onClose={() => {
                setShowAddModal(false);
                void loadReferences();
              }}
            />
          </Modal.Body>
        </Modal>
      </main>
    </div>
  );
};

export default ReferenceFeedbackListPage;
